import React, { useCallback, useContext } from 'react';
import { OnLinkClickType, S4AMastheadProvider } from '@spotify-internal/s4x-masthead';
import { useRouter } from 'next/router';

import { SUPPORT_SITE_CLIENT_ID } from 'utils/config';
import { sendCounterMetric, Whats } from 'utils/metrics';
import { pathWithoutMarket } from 'utils/market/market';

import { MarketContext } from './market';
import { useVerticalConfig } from './vertical';

// S4A's contentful configuration uses a different code for locales similar to what we have:
const susiLocaleToContentfulLocale: Record<string, string> = {
  'es-LA': 'es-419',
  'zh-TW': 'zh-Hant-TW',
};

function normalizeLocale(locale: string): string {
  return susiLocaleToContentfulLocale[locale] ?? locale;
}

interface Props {
  children: React.ReactNode;
}

const S4AMastheadProviderWrapper = ({ children }: Props) => {
  const {
    features: { configureMasthead },
  } = useVerticalConfig();
  const { dir, locale, displayNameWithLanguage } = useContext(MarketContext);
  const marketSelectReturnToPath = pathWithoutMarket(useRouter().asPath);

  const onS4ALinkClick = useCallback(({ trackingAttributes }: OnLinkClickType) => {
    // trackingAttributes is usually a tuple of ["Page Context", "Click Context"]
    const actionContext = trackingAttributes[1].toLowerCase().replace(/\s/g, '_');
    sendCounterMetric(Whats.S4X_MASTHEAD, {
      clicked: actionContext,
    });
  }, []);

  if (configureMasthead?.header !== 's4a') {
    return <>{children}</>;
  }

  return (
    <S4AMastheadProvider
      trackCat="Turnkey Support Site"
      clientId={SUPPORT_SITE_CLIENT_ID}
      onLinkClick={onS4ALinkClick}
      language={{
        dir,
        locale: normalizeLocale(locale),
        useCountryRegionPicker: true,
        market: displayNameWithLanguage,
        marketSelectReturnToPath,
      }}
    >
      {children}
    </S4AMastheadProvider>
  );
};

export default S4AMastheadProviderWrapper;
